import React from 'react';
import { FileText, AlertTriangle, CheckCircle2, Clock, RefreshCw, MessageSquare, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import { RetryButton } from './RetryButton';
import './animations.css'; // Import animations from external CSS file

interface RecordingPreparationScreenProps {
  status: string;
  details?: {
    message?: string;
    progress?: number;
    estimated_time_remaining?: number;
  };
  patientName?: string;
  title?: string;
  date?: string;
  onRefresh?: () => void;
  // Retry processing support - shown alongside the existing "Try Again" refresh
  // action when the recording is eligible (see utils/retryEligibility).
  showRetry?: boolean;
  recordingId?: string;
  templateId?: number | string | null;
  onRetryStarted?: (recordingId: string) => void;
  onRetryError?: (recordingId: string, message: string) => void;
}

const RecordingPreparationScreen: React.FC<RecordingPreparationScreenProps> = ({
  status,
  details,
  patientName,
  title,
  date,
  onRefresh,
  showRetry,
  recordingId,
  templateId,
  onRetryStarted,
  onRetryError,
}) => {
  // Prepare content based on status
  const getStatusContent = () => {
    const lowerStatus = status?.toLowerCase() || '';
    console.log(lowerStatus);
    
    switch (lowerStatus) {
      case 'analyzing':
        return {
          title: 'Your recording is being analyzed',
          description: 'Our AI is detecting speakers and analyzing the audio content',
          icon: <MessageSquare className="w-10 h-10 text-yellow-500" />,
          animation: AnalyzingAnimation,
          color: 'yellow',
          bgClass: 'bg-yellow-100',
          borderClass: 'border-yellow-200',
          textClass: 'text-yellow-800',
          progress: details?.progress || 30,
          timeRemaining: details?.estimated_time_remaining || 180
        };
      case 'transcribed':
      case 'transcribing':
        return {
          title: 'Transcribing your recording',
          description: 'Where medical speech becomes structured intelligence.',
          icon: <FileText className="w-10 h-10 text-blue-500" />,
          animation: TranscribingAnimation,
          color: 'blue',
          bgClass: 'bg-blue-100',
          borderClass: 'border-blue-200',
          textClass: 'text-blue-800',
          progress: details?.progress || 80,
          timeRemaining: details?.estimated_time_remaining || 120
        };
      
      case 'ai processing':
        return {
          title: 'Generating medical content',
          description: 'Our AI is analyzing your medical data and creating structured notes',
          icon: <Zap className="w-10 h-10 text-indigo-500" />,
          animation: AIProcessingAnimation,
          color: 'indigo',
          bgClass: 'bg-indigo-100',
          borderClass: 'border-indigo-200',
          textClass: 'text-indigo-800',
          progress: details?.progress || 90,
          timeRemaining: details?.estimated_time_remaining || 60
        };
      
      case 'processing':
        return {
          title: 'Processing your recording',
          description: 'Extracting medical information and preparing your notes',
          icon: <RefreshCw className="w-10 h-10 text-blue-500" />,
          animation: ProcessingAnimation,
          color: 'blue',
          bgClass: 'bg-blue-100',
          borderClass: 'border-blue-200',
          textClass: 'text-blue-800',
          progress: details?.progress || 20,
          timeRemaining: details?.estimated_time_remaining || 90
        };
      
      case 'pending':
        return {
          title: 'Waiting for processing',
          description: 'Your recording is in the queue and will be processed soon',
          icon: <Clock className="w-10 h-10 text-gray-500" />,
          animation: PendingAnimation,
          color: 'gray',
          bgClass: 'bg-gray-100',
          borderClass: 'border-gray-200',
          textClass: 'text-gray-800',
          progress: details?.progress || 10,
          timeRemaining: details?.estimated_time_remaining || 120
        };
      
      case 'upload':
      case 'uploading':
        return {
          title: 'Uploading your recording',
          description: 'Securely transferring your audio to our servers',
          icon: <RefreshCw className="w-10 h-10 text-blue-500" />,
          animation: ProcessingAnimation,
          color: 'blue',
          bgClass: 'bg-blue-100',
          borderClass: 'border-blue-200',
          textClass: 'text-blue-800',
          progress: details?.progress || 30,
          timeRemaining: details?.estimated_time_remaining || 60
        };

      case 'uploaded':
        return {
          title: 'Recording has been uploaded',
          description: 'Your file is ready and waiting for processing',
          icon: <FileText className="w-10 h-10 text-blue-500" />,
          animation: UploadedAnimation,
          color: 'blue',
          bgClass: 'bg-blue-100',
          borderClass: 'border-blue-200',
          textClass: 'text-blue-800',
          progress: details?.progress || 30,
          timeRemaining: details?.estimated_time_remaining || 0
        };
      
      case 'failed':
        return {
          title: 'We encountered an issue',
          description: details?.message || 'Our system was unable to process this recording. Please contact customer support for assistance.',
          icon: <AlertTriangle className="w-10 h-10 text-red-500" />,
          animation: FailedAnimation,
          color: 'red',
          bgClass: 'bg-red-100',
          borderClass: 'border-red-200',
          textClass: 'text-red-800',
          progress: 0,
          timeRemaining: 0
        };
      
      default:
        return {
          title: 'Contact customer support',
          description: details?.message || 'We encountered an issue processing your recording. Please contact customer support for assistance.',
          icon: <AlertTriangle className="w-10 h-10 text-red-500" />,
          animation: FailedAnimation,
          color: 'red',
          bgClass: 'bg-red-100',
          borderClass: 'border-red-200',
          textClass: 'text-red-800',
          progress: 0,
          timeRemaining: 0
        };
    }
  };

  const content = getStatusContent();
  
  // Format time remaining
  const formatTimeRemaining = (seconds: number) => {
    if (seconds <= 0) return '';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes > 0) {
      return `~${minutes} min${minutes > 1 ? 's' : ''} ${remainingSeconds > 0 ? remainingSeconds + 's' : ''} remaining`;
    }
    
    return `~${remainingSeconds} seconds remaining`;
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-3xl shadow-lg border border-white/50 overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-6 py-5">
        <h1 className="text-xl font-semibold text-gray-900">Notes Preparation</h1>
      </div>
      
      {/* Main content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto py-6 sm:py-12 px-3 sm:px-6 flex flex-col items-center justify-center min-h-[80vh]">
          {/* Recipe card style UI */}
          <div className={`w-full bg-white rounded-2xl shadow-xl border ${content.borderClass} overflow-hidden`}>
            {/* Status banner */}
            <div className={`${content.bgClass} px-4 sm:px-6 py-4 border-b ${content.borderClass}`}>
              <div className="flex items-center">
                {/* Use color-specific dot based on status */}
                {status.toLowerCase() === 'analyzing' && (
                  <div className="w-3 h-3 rounded-full bg-yellow-500 animate-pulse mr-2 sm:mr-3 flex-shrink-0"></div>
                )}
                {status.toLowerCase() === 'transcribing' && (
                  <div className="w-3 h-3 rounded-full bg-blue-500 animate-pulse mr-2 sm:mr-3 flex-shrink-0"></div>
                )}
                {status.toLowerCase() === 'transcript_ready' && (
                  <div className="w-3 h-3 rounded-full bg-indigo-500 animate-pulse mr-2 sm:mr-3 flex-shrink-0"></div>
                )}
                {status.toLowerCase() === 'processing' && (
                  <div className="w-3 h-3 rounded-full bg-blue-500 animate-pulse mr-2 sm:mr-3 flex-shrink-0"></div>
                )}
                {status.toLowerCase() === 'failed' && (
                  <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse mr-2 sm:mr-3 flex-shrink-0"></div>
                )}
                {(status.toLowerCase() === 'upload' || status.toLowerCase() === 'uploading') && (
                  <div className="w-3 h-3 rounded-full bg-blue-500 animate-pulse mr-2 sm:mr-3 flex-shrink-0"></div>
                )}
                {status.toLowerCase() === 'uploaded' && (
                  <div className="w-3 h-3 rounded-full bg-blue-500 mr-2 sm:mr-3 flex-shrink-0"></div>
                )}
                {['analyzing', 'transcribing', 'transcript_ready', 'processing', 'failed', 'upload', 'uploading', 'uploaded'].indexOf(status.toLowerCase()) === -1 && (
                  <div className="w-3 h-3 rounded-full bg-gray-500 animate-pulse mr-2 sm:mr-3 flex-shrink-0"></div>
                )}
                
                <span className={`${content.textClass} font-medium text-sm uppercase tracking-wider truncate`}>
                  {status}
                </span>
              </div>
            </div>
            
            {/* Patient info */}
            {patientName && (
              <div className="px-4 sm:px-6 py-4 border-b border-gray-100">
                <h3 className="text-lg font-medium text-gray-900 truncate">{patientName}</h3>
                {title && <p className="text-sm text-gray-600 mt-1 break-words">{title}</p>}
                {date && <p className="text-xs text-gray-500 mt-1">{date}</p>}
              </div>
            )}
            
            {/* Animation and content */}
            <div className="px-6 py-8 flex flex-col items-center">
              <div className="w-32 h-32 mb-6 relative">
                {content.animation && React.createElement(content.animation, { color: content.color })}
              </div>
              
              <h2 className="text-2xl font-bold text-gray-900 text-center mb-3">
                {content.title}
              </h2>
              
              <p className="text-gray-600 text-center mb-8 max-w-md px-2 sm:px-0 break-words w-full">
                {content.description}
              </p>
              
              {/* Progress bar */}
              {content.progress > 0 && (
                <div className="w-full max-w-md px-2 sm:px-0">
                  <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                    <motion.div 
                      className={`h-2.5 rounded-full ${
                        status.toLowerCase() === 'analyzing' ? 'bg-yellow-500' :
                        status.toLowerCase() === 'transcribing' ? 'bg-blue-500' :
                        status.toLowerCase() === 'transcript_ready' ? 'bg-indigo-500' :
                        status.toLowerCase() === 'processing' ? 'bg-blue-500' :
                        status.toLowerCase() === 'upload' || status.toLowerCase() === 'uploading' ? 'bg-blue-500' :
                        status.toLowerCase() === 'uploaded' ? 'bg-blue-400' :
                        'bg-gray-500'
                      }`}
                      initial={{ width: 0 }}
                      animate={{ width: `${content.progress}%` }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">{content.progress}% Complete</span>
                  </div>
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className={`${content.bgClass} px-4 sm:px-6 py-4 border-t ${content.borderClass}`}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className={`text-sm ${
                  status.toLowerCase() === 'analyzing' ? 'text-yellow-700' :
                  status.toLowerCase() === 'transcribing' ? 'text-blue-700' :
                  status.toLowerCase() === 'transcript_ready' ? 'text-indigo-700' :
                  status.toLowerCase() === 'processing' ? 'text-blue-700' :
                  status.toLowerCase() === 'upload' || status.toLowerCase() === 'uploading' || status.toLowerCase() === 'uploaded' ? 'text-blue-700' :
                  status.toLowerCase() === 'failed' ? 'text-red-700' :
                  'text-gray-700'
                }`}>
                  <p className="break-words">
                    {status.toLowerCase() === 'failed' ? 
                      'Please contact customer support with error code: ' + (Date.now().toString().slice(-6)) : 
                      'We\'re cooking up something interesting for you!'}
                  </p>
                </div>
                
                {(status.toLowerCase() === 'failed' || showRetry) && (
                  <div className="flex gap-2 flex-wrap">
                    {showRetry && recordingId && (
                      <RetryButton
                        recordingId={recordingId}
                        templateId={templateId}
                        recordingTitle={title}
                        size="md"
                        stopPropagation={false}
                        onRetryStarted={onRetryStarted}
                        onRetryError={onRetryError}
                      />
                    )}
                    {status.toLowerCase() === 'failed' && onRefresh && (
                      <button
                        onClick={onRefresh}
                        className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 font-medium rounded-lg transition-colors text-sm w-full sm:w-auto"
                      >
                        Try Again
                      </button>
                    )}
                    <button
                      onClick={() => window.open('mailto:support@scribedo.com?subject=Error%20' + Date.now().toString().slice(-6))}
                      className="px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 font-medium rounded-lg transition-colors flex items-center justify-center text-sm w-full sm:w-auto"
                    >
                      <svg className="w-4 h-4 mr-2 flex-shrink-0" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21 12C21 16.97 16.97 21 12 21C7.03 21 3 16.97 3 12C3 7.03 7.03 3 12 3C16.97 3 21 7.03 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M12 12H12.01M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Contact Support
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Tips section */}
          <div className="mt-8 sm:mt-12 w-full bg-gray-50 rounded-xl p-4 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3 sm:mb-4">While you wait...</h3>
            <ul className="space-y-2 sm:space-y-3">
              <li className="flex items-start">
                <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm sm:text-base text-gray-700">You can start preparing for another recording</span>
              </li>
              <li className="flex items-start">
                <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm sm:text-base text-gray-700">Review existing notes from previous recordings</span>
              </li>
              <li className="flex items-start">
                <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm sm:text-base text-gray-700">Switch to another recording while this one is processing</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Animation components
const AnalyzingAnimation = ({ color = 'yellow' }) => (
  <div className="w-full h-full relative">
    <div className="absolute inset-0 rounded-full border-4 border-yellow-200"></div>
    <div className="absolute inset-0 rounded-full border-4 border-yellow-500 border-t-transparent animate-spin"></div>
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="w-16 h-16 bg-yellow-50 rounded-full flex items-center justify-center animate-pulse">
        <div className="w-8 h-8 bg-yellow-500/30 rounded-full"></div>
      </div>
    </div>
    
    {/* Sound waves animation */}
    <div className="absolute inset-0 flex items-center justify-center">
      {[1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="absolute w-full h-full border-2 border-yellow-300 rounded-full opacity-0"
          style={{
            animation: `soundWave 2s infinite ${i * 0.4}s`,
          }}
        />
      ))}
    </div>
  </div>
);

const TranscribingAnimation = ({ color = 'blue' }) => (
  <div className="w-full h-full relative">
    <div className="absolute inset-0 rounded-full border-4 border-blue-200"></div>
    <div className="absolute inset-0 rounded-full border-4 border-blue-500 border-t-transparent animate-spin"></div>
    
    {/* Text slicing effect */}
    <div className="absolute inset-0 flex flex-col items-center justify-center">
      <div className="w-4/5 space-y-2 relative">
        {[1, 2, 3].map((i) => (
          <div 
            key={i}
            className="h-2 bg-blue-300 rounded-full flex overflow-hidden"
            style={{
              width: `${70 + Math.random() * 30}%`,
            }}
          >
            {/* Slicing animation overlay */}
            <div 
              className="h-full w-3 bg-blue-500"
              style={{
                animation: `slicingMotion 1.5s infinite ${i * 0.3}s`,
                transform: 'translateX(-50%)',
                position: 'relative',
                left: i % 2 === 0 ? '100%' : '80%'
              }}
            />
          </div>
        ))}
      </div>
    </div>
  </div>
);

const ProcessingAnimation = ({ color = 'indigo' }) => (
  <div className="w-full h-full relative">
    <div className="absolute inset-0 rounded-full border-4 border-indigo-200"></div>
    <div className="absolute inset-0 rounded-full border-t-4 border-r-4 border-indigo-600 animate-spin"></div>
    <div className="absolute inset-2 rounded-full border-2 border-l-2 border-indigo-400 animate-spin"></div>
    
    {/* Sparkling dots */}
    <div className="absolute inset-0 flex items-center justify-center">
      {[0, 1, 2, 3, 4, 5].map((index) => (
        <motion.div
          key={index}
          className="absolute w-2 h-2 bg-indigo-500 rounded-full"
          initial={{ opacity: 0 }}
          animate={{ 
            opacity: [0, 1, 0],
            x: Math.cos(index * Math.PI / 3) * 20,
            y: Math.sin(index * Math.PI / 3) * 20
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            delay: index * 0.3,
          }}
        />
      ))}
      <div className="w-10 h-10 bg-indigo-100 rounded-full"></div>
    </div>
  </div>
);

// AI Processing Animation - magical effect
const AIProcessingAnimation = ({ color = 'indigo' }) => (
  <div className="w-full h-full relative">
    <div className="absolute inset-0 rounded-full border-4 border-purple-200"></div>
    
    {/* Magical pulsing ring */}
    <div className="absolute inset-0 rounded-full border-4 border-purple-400 opacity-30"
      style={{ animation: 'magicPulse 3s ease-in-out infinite' }}></div>
    
    {/* Inner glowing orb */}
    <div className="absolute inset-4 rounded-full bg-gradient-to-br from-indigo-400 to-purple-600 opacity-70"
      style={{ animation: 'pulse 2s ease-in-out infinite' }}></div>
    
    {/* Central spark */}
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="w-8 h-8 bg-white rounded-full opacity-80"></div>
      <div className="absolute w-12 h-12 flex items-center justify-center">
        <svg viewBox="0 0 24 24" className="w-6 h-6 text-purple-500" style={{ animation: 'magicRotate 8s linear infinite' }}>
          <path fill="currentColor" d="M12,3L2,12H5V20H19V12H22L12,3M12,7.7L16,11H14V15H10V11H8L12,7.7Z" />
        </svg>
      </div>
    </div>
    
    {/* Magical spinning stars */}
    <div className="absolute inset-0">
      {[0, 1, 2, 3, 4, 5].map((index) => (
        <div
          key={`star-${index}`}
          className="absolute w-3 h-3"
          style={{
            top: `${20 + Math.random() * 60}%`,
            left: `${20 + Math.random() * 60}%`,
            opacity: 0.7,
            transform: 'rotate(35deg)',
            animation: `aiProcessingDot ${1 + Math.random() * 2}s ease-in-out ${index * 0.3}s infinite`
          }}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" className="text-purple-300">
            <path d="M12,2L15.09,8.26L22,9.27L17,14.14L18.18,21.02L12,17.77L5.82,21.02L7,14.14L2,9.27L8.91,8.26L12,2Z" />
          </svg>
        </div>
      ))}
    </div>
    
    {/* Orbiting magic particles */}
    <div className="absolute inset-0 origin-center" style={{ animation: 'magicRotate 10s linear infinite' }}>
      {[0, 1, 2, 3, 4, 5].map((index) => (
        <div
          key={index}
          className="absolute"
          style={{
            top: 'calc(50% - 3px)',
            left: 'calc(50% - 3px)',
            transform: `rotate(${index * 60}deg) translateY(-35px)`,
          }}
        >
          <div className="h-2 w-2 bg-indigo-400 rounded-full"
            style={{ 
              animation: `pulse 1.5s ease-in-out ${index * 0.3}s infinite`
            }}>
          </div>
        </div>
      ))}
    </div>
    
    {/* Magic sparks shooting out */}
    <div className="absolute inset-0">
      {[0, 1, 2, 3].map((index) => {
        const angle = index * 90;
        return (
          <div
            key={`spark-${index}`}
            className="absolute top-1/2 left-1/2 w-1.5 h-1.5"
            style={{
              transform: `rotate(${angle}deg) translateY(-45px)`,
            }}
          >
            <div className="absolute w-1.5 h-5 bg-gradient-to-t from-purple-500 to-transparent"
              style={{ 
                animation: `magicSpark 1.5s ease-in-out ${index * 0.2}s infinite`
              }}
            ></div>
          </div>
        );
      })}
    </div>
    
    {/* Magic dust particles */}
    <div className="absolute inset-0">
      {[0, 1, 2, 3, 4, 5, 6, 7].map((index) => (
        <div
          key={`dust-${index}`}
          className="absolute w-1 h-1 bg-purple-300 rounded-full"
          style={{
            top: `${30 + Math.random() * 40}%`,
            left: `${30 + Math.random() * 40}%`,
            opacity: 0.7,
            animation: `aiProcessingDot 2s ease-in-out ${index * 0.5}s infinite`
          }}
        />
      ))}
    </div>
  </div>
);

// Uploaded Animation - file waiting for processing
const UploadedAnimation = ({ color = 'blue' }) => (
  <div className="w-full h-full relative">
    <div className="absolute inset-0 rounded-full border-4 border-blue-200"></div>
    
    {/* Central document icon */}
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="w-24 h-24 relative">
        {/* Document background */}
        <div className="absolute inset-0 bg-blue-100 rounded-lg"></div>
        
        {/* Document fold */}
        <div className="absolute top-0 right-0 w-0 h-0 border-t-[8px] border-r-[8px] border-t-transparent border-r-blue-200"></div>
        
        {/* Document lines */}
        {[1, 2, 3, 4].map((i) => (
          <div 
            key={i} 
            className="absolute h-1 bg-blue-300 rounded-full"
            style={{ 
              left: '20%', 
              right: '20%',
              top: `${30 + i * 15}%`,
              opacity: 0.7,
              animation: i % 2 === 0 ? 'pulse 2s infinite' : 'none'
            }}
          ></div>
        ))}
        
        {/* Waiting animation */}
        <div className="absolute bottom-2 right-2 flex space-x-1">
          {[0, 1, 2].map((i) => (
            <div 
              key={i}
              className="w-2 h-2 bg-blue-500 rounded-full"
              style={{ 
                animation: `loadingDot 1.5s infinite ${i * 0.3}s`
              }}
            ></div>
          ))}
        </div>
        
        {/* Check mark (upload completed) */}
        <div className="absolute top-2 left-2">
          <svg viewBox="0 0 24 24" className="w-5 h-5 text-blue-500">
            <path fill="currentColor" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" />
          </svg>
        </div>
      </div>
    </div>
    
    {/* Subtle pulsing ring */}
    <div className="absolute inset-0 rounded-full border-2 border-blue-300 opacity-30"
      style={{ animation: 'pulse 3s ease-in-out infinite' }}></div>
  </div>
);

const FailedAnimation = ({ color = 'red' }) => (
  <div className="w-full h-full relative">
    <div className="absolute inset-0 rounded-full border-4 border-red-200"></div>
    
    {/* Outer alert circle that pulses */}
    <div className="absolute inset-0 rounded-full border-4 border-red-400 opacity-50"
      style={{ animation: 'errorPulse 3s ease-in-out infinite' }}></div>
    
    {/* Inner container */}
    <div className="absolute inset-0 flex items-center justify-center">
      <div 
        className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center"
        style={{ animation: 'errorShake 0.82s cubic-bezier(.36,.07,.19,.97) 1s both' }}
      >
        {/* Support icon - headset/phone */}
        <div className="relative w-8 h-8" style={{ animation: 'errorFloat 2s ease-in-out infinite' }}>
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2.5" 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            className="text-red-500"
          >
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
          </svg>
        </div>
      </div>
    </div>
    
    {/* Error code numbers rotating around */}
    <div className="absolute inset-0 origin-center"
      style={{ animation: 'errorRotate 12s linear infinite' }}>
      {[0, 1, 2].map((index) => (
        <div
          key={index}
          className="absolute"
          style={{
            top: 'calc(50% - 3px)',
            left: 'calc(50% - 3px)',
            transform: `rotate(${index * 120}deg) translateY(-30px)`,
          }}
        >
          <div 
            className="text-xs font-mono font-bold px-2 py-0.5 bg-red-400 text-white rounded"
            style={{ 
              animation: `warningFlash 1.5s ease-in-out ${index * 0.5}s infinite`
            }}
          >
            {index === 0 ? 'ERR' : index === 1 ? '404' : '500'}
          </div>
        </div>
      ))}
    </div>
    
    {/* Radial connection lines */}
    <div className="absolute inset-0 flex items-center justify-center">
      {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, index) => (
        <div
          key={index}
          className="absolute opacity-70"
          style={{
            width: index % 2 === 0 ? '2px' : '1.5px',
            height: index % 2 === 0 ? '12px' : '8px',
            background: 'linear-gradient(to top, transparent, #f87171)',
            transform: `rotate(${angle}deg) translateY(-25px)`,
            animation: `errorBlink 1.5s ease-in-out ${index * 0.1}s infinite`
          }}
        />
      ))}
    </div>
    
    {/* "SOS" morse code effect dots */}
    <div className="absolute inset-0">
      {[0, 1, 2, 3, 4].map((index) => (
        <div
          key={index}
          className="absolute w-1.5 h-1.5 bg-red-500 rounded-full"
          style={{
            top: `${20 + Math.random() * 60}%`,
            left: `${20 + Math.random() * 60}%`,
            opacity: 0.7,
            animation: `errorPulse ${index < 3 ? '0.5s' : '1.5s'} ease-in-out ${index * 0.2}s infinite`
          }}
        />
      ))}
    </div>
  </div>
);

// Pending Animation - waiting state
const PendingAnimation = ({ color = 'gray' }) => (
  <div className="w-full h-full relative">
    <div className="absolute inset-0 rounded-full border-4 border-gray-200"></div>
    
    {/* Subtle pulsing ring */}
    <div className="absolute inset-0 rounded-full border-2 border-gray-300 opacity-30"
      style={{ animation: 'pulse 3s ease-in-out infinite' }}></div>
    
    {/* Central clock */}
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center">
        <div className="relative w-16 h-16">
          {/* Clock face */}
          <div className="absolute inset-0 rounded-full border-2 border-gray-300"></div>
          
          {/* Clock tick marks */}
          {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((angle, i) => (
            <div 
              key={i}
              className="absolute w-1 h-1.5 bg-gray-400"
              style={{
                top: '2px',
                left: 'calc(50% - 0.5px)',
                transformOrigin: 'bottom center',
                transform: `rotate(${angle}deg) translateY(-6px)`
              }}
            ></div>
          ))}
          
          {/* Hour hand */}
          <div 
            className="absolute top-1/2 left-1/2 w-1 h-5 bg-gray-700 rounded-full"
            style={{
              transformOrigin: 'center bottom',
              transform: 'translate(-50%, -100%) rotate(0deg)',
              animation: 'clockHour 4s linear infinite'
            }}
          ></div>
          
          {/* Minute hand */}
          <div 
            className="absolute top-1/2 left-1/2 w-0.5 h-7 bg-gray-500 rounded-full"
            style={{
              transformOrigin: 'center bottom',
              transform: 'translate(-50%, -100%) rotate(0deg)',
              animation: 'clockMinute 2s linear infinite'
            }}
          ></div>
          
          {/* Center dot */}
          <div className="absolute top-1/2 left-1/2 w-1.5 h-1.5 bg-gray-600 rounded-full transform -translate-x-1/2 -translate-y-1/2"></div>
        </div>
      </div>
    </div>
    
    {/* Waiting dots animation */}
    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
      {[0, 1, 2].map((i) => (
        <div 
          key={i}
          className="w-2 h-2 bg-gray-400 rounded-full"
          style={{ 
            animation: `loadingDot 1.5s infinite ${i * 0.3}s`
          }}
        ></div>
      ))}
    </div>
    
    {/* Animated waiting icon */}
    <div className="absolute top-5 right-5">
      <div className="relative w-6 h-6">
        <svg 
          viewBox="0 0 24 24" 
          className="text-gray-400" 
          style={{ animation: 'pulse 2s infinite ease-in-out' }}
        >
          <path fill="currentColor" d="M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z" />
        </svg>
      </div>
    </div>
  </div>
);

export default RecordingPreparationScreen;